import PieChartRenderer from './PieChartRenderer';
import BarChartRenderer from './BarChartRenderer';
import LineChartRenderer from './LineChartRenderer';
import ScatterRenderer from './ScatterRenderer';
import AreaChartRenderer from './AreaChartRenderer';
import DoughnutRenderer from './DoughnutRenderer';
import StackedBarRenderer from './StackedBarRenderer';
import GroupedBarRenderer from './GroupedBarRenderer';
import KpiRenderer from './KpiRenderer';
import TableRenderer from './TableRenderer';
import SummaryRowRenderer from './SummaryRowRenderer';

// All chart types map to a D3 renderer. stacked_bar / grouped_bar render their
// `series` breakdown as true stacked / side-by-side segments with a legend
// (falling back to a plain bar when the data carries no series). histogram still
// reuses BarChartRenderer — it is single-dimension {label, value} data.
export const RENDERERS = {
  pie: PieChartRenderer,
  bar: BarChartRenderer,
  line: LineChartRenderer,
  scatter: ScatterRenderer,
  area: AreaChartRenderer,
  doughnut: DoughnutRenderer,
  stacked_bar: StackedBarRenderer,
  grouped_bar: GroupedBarRenderer,
  histogram: BarChartRenderer,
  // Single-value shape: one number or short text, not a category-value series.
  kpi: KpiRenderer,
  // Tabular shape: typed columns and string rows, capped server-side.
  table: TableRenderer,
  // Composite: a strip of tiles, each answered as its own widget. This renderer
  // draws a STORED strip (export, first paint); the live one is SummaryRow, which
  // owns a query per tile. Registered here for the same reason as the rest — it is
  // what makes the picker offer the type.
  summary_row: SummaryRowRenderer,
};

// Types whose result is assembled from other types rather than from one agent call,
// so the card delegates to a live container instead of running a query itself. Mirrors
// shared/widget_schemas.py::COMPOSITE_WIDGET_TYPES, which is the backend's copy.
const COMPOSITE_TYPES = new Set(['summary_row']);

export function isCompositeWidgetType(widgetTypeId) {
  return COMPOSITE_TYPES.has(widgetTypeId);
}

// Types whose result is the whole payload rather than the `data` series. KPI and
// table payloads carry the schema's `data: []` default — truthy — so the old
// `payload.data || payload` fallback would hand the renderer an empty list.
const WHOLE_PAYLOAD_TYPES = new Set(['kpi', 'table', 'summary_row']);

// What a renderer for this type is given: the `data` series for the chart types,
// the payload itself for single-value types. Tolerates a bare series (stored
// widgets predating the wrapped shape) by passing it through.
export function rendererInput(widgetTypeId, payload) {
  if (payload == null) return payload;
  if (WHOLE_PAYLOAD_TYPES.has(widgetTypeId)) return payload;
  return Array.isArray(payload) ? payload : payload.data ?? payload;
}

// A type with no renderer is unsupported in this build. It used to fall back to
// the bar chart, which drew a saved KPI or Table widget as bars the moment its
// family flag went on — a wrong picture is worse than an honest "not supported".
export function getRenderer(widgetTypeId) {
  // Own keys only: a stored id like "toString" must not resolve to an inherited
  // function and be handed to React as a component.
  return isImplementedWidgetType(widgetTypeId) ? RENDERERS[widgetTypeId] : null;
}

// The picker offers a type only when it can be drawn, so "implemented" has one
// definition: it has a renderer. Registering one here is what makes a type offerable.
export function isImplementedWidgetType(widgetTypeId) {
  return Object.prototype.hasOwnProperty.call(RENDERERS, widgetTypeId);
}
